/**
 * DscrSizerScreen — the DSCR "Quick Price" sizer, as a scenario workspace.
 *
 * SAME WORKSPACE AS THE RTL/GUC SIZER, and deliberately so (SizerWorkspace): a
 * priced scenario lives on one page with two modes — Review (the stored answer:
 * KPI strip, the result cards flowed wide, the inputs that priced it) and Edit
 * inputs (the form as one card per subject, with a sticky rail: the re-price
 * card, then the answer, dimmed and tagged Stale once the form drifts). The
 * header carries the name, mode toggle, version picker and ⋯ menu; the old
 * fixed footer bar and resumed-scenario banner are gone.
 *
 * The rail and not a full-width takeover: the old `sensitivity` matrix could
 * not fit a rail; the backend retired it (2026-08-18) for a 5-column
 * `outcomeMatrix` that can, and pricing is an ITERATE loop — the inputs stay
 * beside their answer. Review mode is where the cards get the page's width.
 *
 * Three things this screen must never do (pricing-scenario-sizers.md §2):
 *   • Recompute a matrix cell. Every cell is a full server-side reprice; a
 *     changed input means RE-POSTING the scenario.
 *   • Treat a 422 as a failure. `missingRequirements` is a structured gate
 *     naming the inputs whose absence would silently skip an LLPA table and
 *     misprice the loan, and `ruralGate` is a determination carrying its own
 *     answer. Both render on the form, not as a snackbar.
 *   • Auto-set `ruralExceptionRequested`. A rural exception is a user action,
 *     and it rides onto the persisted scenario — so it is asked for, never
 *     assumed. (Once asked for, it stays with THAT scenario: re-pricing it
 *     carries the request rather than re-hitting the gate.)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import Box from "@mui/material/Box";

import { dscrApi } from "../../api/dscrApi";
import { sizerApi, readScenarioId } from "../../api/sizerApi";
import {
  INITIAL_FORM,
  DEMO_FORM,
  buildDscrPayload,
  dscrPayloadToForm,
} from "./dscrConstants";
import { dscrSheetLabel } from "../sizersShared/sizerSelectOptions";
import LoanDetailsSection from "./sections/LoanDetailsSection";
import BorrowerSection from "./sections/BorrowerSection";
import PropertySection from "./sections/PropertySection";
import IncomeExpensesSection from "./sections/IncomeExpensesSection";
import DscrResultsColumn from "../sizersShared/DscrResultsColumn";
import { DscrKpis, DscrPanels } from "../sizersShared/DscrReview";
import DscrEntryTabs from "../sizersShared/DscrEntryTabs";
import useSizerCorrespondents from "../sizersShared/useSizerCorrespondents";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import {
  FormCard,
  SecondaryButton,
  ActionNote,
  SizerDevBar,
  useSizerDevTools,
  SizerStateSpy,
  scrollSizerToTop,
  SZ,
} from "../sizersShared/sizerUi";
import { Callout, MissingRequirementsCallout } from "../sizersShared/sizerResultUi";
import SizerWorkspace from "../sizersShared/workspace/SizerWorkspace";
import {
  WORKSPACE_MODES,
  pricedToastText,
  useWorkspaceMode,
} from "../sizersShared/workspace/workspaceModel";
import { scenarioInputSummary } from "../sizersShared/scenarioModel";
import { canBeDelayedPurchase } from "../../utils/loans/delayedPurchase";

/**
 * Flatten the 422 body into `{fieldName: note}` so each named input renders its
 * own blocking error.
 *
 * TWO SHAPES, both legitimate. The LLPA gates arrive as
 * `{llpa, missing: [names], note}` — one entry covering several fields. The
 * newer input gates (`address.zip`) arrive as `{field, reason}` — one entry, one
 * field. Reading only the first shape is what made the ZIP gate render as an
 * empty heading over "undefined".
 */
function gateFieldsFrom(missing) {
  return (missing || []).reduce((acc, m) => {
    (m.missing || []).forEach((f) => {
      acc[f] = m.note;
    });
    if (m.field) acc[m.field] = m.reason || m.note;
    return acc;
  }, {});
}

/**
 * A failure's message as words. FastAPI's own validation 422 carries `detail`
 * as an ARRAY of `{loc, msg}` — `String()` on it rendered "[object Object]".
 */
function errorText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) {
    const lines = d.map((x) =>
      x?.msg
        ? [(x.loc || []).filter((l) => l !== "body").join("."), x.msg]
            .filter(Boolean)
            .join(": ")
        : String(x),
    );
    return lines.join("; ") || fallback;
  }
  if (d && typeof d === "object") return d.message || JSON.stringify(d);
  return String(d || e?.message || fallback);
}

/* The inputs the model requires with no default and no 422 gate of their own:
   left blank, the payload builder posts "" / 0 and the run either 400s or
   prices a $0 loan. ZIP, FICO and rents have server gates that render on the
   field, so they are left to those. */
const REQUIRED = [
  ["loanPurpose", "Purpose"],
  ["amortizationType", "Amortization"],
  ["requestedLoanAmount", "Loan amount"],
  ["asIsValue", "As-is value"],
];
const isBlank = (v) => v === "" || v == null;

export default function DscrSizerScreen() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [missingRequirements, setMissingRequirements] = useState([]);
  // The rural 422's body — a determination plus the market block, not an error.
  const [ruralGate, setRuralGate] = useState(null);
  const [ruralNote, setRuralNote] = useState("");
  const [busy, setBusy] = useState(false);
  // The ladder rung a click-to-re-price is in flight for — drives the spinner
  // ON that row, so the feedback is where the click was.
  const [reanchoring, setReanchoring] = useState(null);
  const correspondents = useSizerCorrespondents();
  const { spyOpen, toggleSpy } = useSizerDevTools();
  const { enqueueSnackbar } = useSnackbar();

  // The form state at the moment pricing was last run — what the result
  // describes. With `loadedForm` it is the workspace's baseline.
  const [pricedForm, setPricedForm] = useState(null);
  // …and WHEN it was run, stamped above the rail.
  const [pricedAt, setPricedAt] = useState(null);
  const shellRef = useRef(null);
  // The persisted scenario this screen is pricing. Sending the id back is what
  // makes the next run a VERSION of this scenario rather than a new object. A
  // ref because it must survive a reprice without being a render input.
  const scenarioIdRef = useRef(null);
  /* Set right before this screen moves its OWN URL after a price (blank →
     `?scenario=<id>`, or dropping `&version=`). The resume effect then skips
     its re-seed: the form already holds what just priced. */
  const skipResumeRef = useRef(false);

  // Put the top back in view after a price so the answer is read from its
  // first line. Keyed on `pricedAt`, stamped fresh every run.
  useEffect(() => {
    if (pricedAt) scrollSizerToTop(shellRef.current);
  }, [pricedAt]);

  /* RESUME — `?scenario=<id>` from Priced Scenarios. DSCR Quick Entry's
     scenarios resume HERE too: same `dscr_quick` payload, and this form is the
     superset, so nothing stored can fail to load. */
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  /* Which version is loaded lives in the URL as well as in state, so a reload
     lands on the same inputs and a pasted link carries them. */
  const setVersionParam = (version) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (version) next.set("version", String(version));
        else next.delete("version");
        return next;
      },
      { replace: true },
    );
  // `readScenarioId` and not `.get()`: `?scenario=null` is a truthy string that
  // would fetch `/pricing/scenarios/null` and 500.
  const resumeId = readScenarioId(searchParams.get("scenario"));
  // A SPECIFIC version's inputs ("Open this version"). Pricing from here still
  // appends the next version — never a rollback.
  const resumeVersion = Number(searchParams.get("version")) || null;
  // Review | Edit inputs — `?mode=`, Review by default on a resumed scenario.
  const [mode, setMode] = useWorkspaceMode(Boolean(resumeId));
  // The resumed scenario's doc — what the workspace header reads.
  const [resumedDoc, setResumedDoc] = useState(null);
  // Which version's inputs are on screen, and what they were when they landed.
  const [loadedVersion, setLoadedVersion] = useState(null);
  const [loadedForm, setLoadedForm] = useState(null);

  /**
   * Seed the form from one stored version AND REPLAY ITS STORED RESULT —
   * `PricingScenarioVersion.result` is the full priced response, so looking at
   * a run never re-POSTs (which would price against TODAY's sheet and append a
   * version). `pricedAt` comes from the STORED timestamp, never `now`.
   */
  const seedFromScenario = useCallback((payload, version, priced, pricedWhen) => {
    const seeded = dscrPayloadToForm(payload);
    if (!seeded) return;
    setForm(seeded);
    setLoadedForm(seeded);
    setLoadedVersion(version ?? null);
    setResult(priced || null);
    setPricedForm(priced ? seeded : null);
    setPricedAt(priced ? pricedWhen || null : null);
    // Gates and errors belonged to whatever was on screen before.
    setRuralGate(null);
    setMissingRequirements([]);
    setErr("");
  }, []);

  useEffect(() => {
    if (!resumeId) return;
    if (skipResumeRef.current) {
      skipResumeRef.current = false;
      return;
    }
    let live = true;
    sizerApi
      .getScenario(resumeId)
      .then((doc) => {
        if (!live) return;
        scenarioIdRef.current = doc?.scenarioId || resumeId;
        setResumedDoc(doc || null);
        const pick = resumeVersion
          ? (doc?.versions || []).find((v) => v.version === resumeVersion)
          : null;
        // The doc's top-level `result` mirrors the LATEST version; a deep-linked
        // version carries its own. Either way the answer replays from storage.
        const current = (doc?.versions || []).find(
          (v) => v.version === doc?.currentVersion,
        );
        seedFromScenario(
          pick?.scenario || doc?.scenario,
          pick?.version || doc?.currentVersion,
          pick ? pick.result : doc?.result,
          (pick || current)?.pricedAt || doc?.updatedAt,
        );
      })
      .catch((e) => {
        if (live) setErr(errorText(e, "Could not load that scenario"));
      });
    return () => {
      live = false;
    };
  }, [resumeId, resumeVersion, seedFromScenario]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const missingRequired = [
    ...REQUIRED.filter(([k]) => isBlank(form[k])).map(([, label]) => label),
    // Asked (and required) only on a cash-out refi.
    ...(canBeDelayedPurchase(form.loanPurpose) &&
    typeof form.delayedPurchase !== "boolean"
      ? ["Delayed purchase?"]
      : []),
  ];
  const blockedReason = missingRequired.length
    ? `Fill the required fields marked * — ${missingRequired.join(", ")} ${
        missingRequired.length === 1 ? "is" : "are"
      } missing.`
    : null;

  /**
   * Price the scenario.
   *
   * `rural` is passed ONLY when the user acted on the rural gate. A scenario
   * that already carries a requested exception (from that action, or stored on
   * a resumed scenario) sends it again — otherwise every re-price, ladder click
   * included, landed back on the gate.
   *
   * `overrideForm` exists for the ladder's click-to-re-anchor: `setForm` is
   * async, so pricing off `form` there would post the PREVIOUS coupon.
   */
  async function run(rural, overrideForm) {
    const f = overrideForm || form;
    const ruralReq =
      rural ||
      (f.ruralExceptionRequested
        ? { requested: true, note: f.ruralExceptionNote }
        : null);
    const prevResult = result;
    const prevVersion = loadedVersion;
    setBusy(true);
    setErr("");
    setMissingRequirements([]);
    if (!rural) setRuralGate(null);
    try {
      const r = await dscrApi.quickPrice({
        ...buildDscrPayload(f, { rural: ruralReq }),
        // Reprice as the next version of the same persisted scenario.
        ...(scenarioIdRef.current ? { scenarioId: scenarioIdRef.current } : null),
      });
      // Persistence is best-effort server-side: no id back means the run wasn't
      // stored, and the next price simply starts a new scenario.
      if (r?.scenarioId) scenarioIdRef.current = r.scenarioId;
      // The exception the user just asked for now belongs to this scenario.
      const exception = rural?.requested
        ? { ruralExceptionRequested: true, ruralExceptionNote: rural.note || null }
        : null;
      const priced = exception ? { ...f, ...exception } : f;
      if (exception) setForm((prev) => ({ ...prev, ...exception }));
      setResult(r);
      setRuralGate(null);
      setPricedForm(priced);
      setPricedAt(new Date().toISOString());
      // What just priced IS the current version now.
      setLoadedForm(priced);
      if (r?.scenarioVersion) setLoadedVersion(r.scenarioVersion);
      enqueueSnackbar(
        pricedToastText({
          scenarioType: "dscr_quick",
          data: r,
          prevResult,
          prevVersion,
        }),
        {
          variant: "success",
          action: r?.scenarioId
            ? () => (
                <Box
                  component="button"
                  type="button"
                  onClick={() => setMode(WORKSPACE_MODES.REVIEW)}
                  sx={{
                    border: 0,
                    background: "none",
                    color: "inherit",
                    font: "inherit",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  View results
                </Box>
              )
            : undefined,
        },
      );
      if (r?.scenarioId) {
        // The workspace header reads the doc (name, versions, status) — re-read
        // it so the new version is in the picker.
        sizerApi
          .getScenario(r.scenarioId)
          .then((doc) => doc && setResumedDoc(doc))
          .catch(() => {});
        // A blank sizer becomes this scenario's workspace; a resumed one drops
        // a stale `&version=`. Either moves the URL, which must not re-seed.
        if (resumeId !== r.scenarioId || searchParams.get("version")) {
          skipResumeRef.current = true;
          setSearchParams(
            { scenario: r.scenarioId, mode: WORKSPACE_MODES.EDIT },
            { replace: true },
          );
        }
      }
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data;
      if (status === 422 && body?.ruralGate) {
        // Not an error — a determination that blocks pricing until an exception
        // is requested. The rail renders it while the form asks for the note.
        setRuralGate(body.ruralGate);
        setResult(null);
      } else if (status === 422 && body?.missingRequirements) {
        // Also not an error — a gate. The inputs it names are the fix.
        setMissingRequirements(body.missingRequirements);
        setResult(null);
      } else {
        setErr(errorText(e, "Failed to price this scenario."));
      }
    } finally {
      setBusy(false);
      setReanchoring(null);
    }
  }

  /**
   * Re-anchor on a ladder click: price THIS rung. RE-POSTS rather than
   * re-deriving — every rung's LLPA bucket, clamp and DSCR band are the
   * server's to decide, and the requested coupon persists onto the scenario.
   */
  const reanchor = (coupon) => {
    const next = { ...form, requestedCoupon: coupon };
    setForm(next);
    setReanchoring(coupon);
    run(null, next);
  };

  /** Back to an unsaved scenario — the next price starts its own history. */
  const resetScenario = () => {
    scenarioIdRef.current = null;
    setResumedDoc(null);
    setLoadedVersion(null);
    setLoadedForm(null);
    setResult(null);
    setPricedForm(null);
    setPricedAt(null);
    setRuralGate(null);
    setRuralNote("");
    setMissingRequirements([]);
    setErr("");
  };

  const gateFields = gateFieldsFrom(missingRequirements);

  // The line above the rail describes the scenario that PRICED, not whatever the
  // form holds now.
  const pricedContext = useMemo(() => {
    const v = pricedForm || {};
    const r = result?.result || {};
    return [
      v.loanPurpose,
      r.tier ? `${r.tier} tier` : null,
      // The sheet that PRICED, as the backend reports it.
      r.cpId ? `priced off ${dscrSheetLabel(r.cpId)}` : null,
      result?.scenarioVersion ? `v${result.scenarioVersion}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }, [pricedForm, result]);

  /* What the numbers on screen were priced from — the last run this session,
     or the version the form was loaded from. */
  const baseline = pricedForm || loadedForm || null;

  // The version on screen, for Review mode's KPIs and inputs grid — its STORED
  // scenario, not the form.
  const shownRun = (resumedDoc?.versions || []).find(
    (v) => v.version === loadedVersion,
  );
  const shownScenario =
    shownRun?.scenario ||
    (loadedVersion === resumedDoc?.currentVersion ? resumedDoc?.scenario : null);

  return (
    <SizerWorkspace
      rootRef={shellRef}
      adminTools={
        <SizerDevBar
          variant="menu"
          /* A different scenario, not a revision of the one on screen — drop
             the persisted id (and the workspace) so the next price starts its
             own history. */
          onFillDemo={() => {
            resetScenario();
            setForm(DEMO_FORM);
            if (resumeId) navigate("/sizers/dscr");
          }}
          spyOpen={spyOpen}
          onToggleSpy={toggleSpy}
        />
      }
      title="DSCR Sizer"
      subtitle="Price a DSCR scenario against the live rate engine."
      /* Full Entry and Quick Entry are two depths of ONE sizer — same endpoint,
         same results column, same scenario store. Blank sizer only. */
      headerTabs={<DscrEntryTabs />}
      scenarioId={resumeId}
      doc={resumedDoc}
      onDocChange={setResumedDoc}
      typeLabel="DSCR"
      shownVersion={loadedVersion}
      onLoadVersion={(v) => {
        // That run's OWN stored result, so the page shows what v1 actually
        // priced at rather than what today's sheet says about v1's inputs.
        seedFromScenario(v.scenario, v.version, v.result, v.pricedAt);
        setVersionParam(v.version);
      }}
      mode={mode}
      onModeChange={setMode}
      onDuplicate={
        resumeId
          ? () => {
              // Same inputs, new scenario. A rural exception was asked for on
              // THAT scenario, so it is not copied — the copy asks again.
              resetScenario();
              setForm((prev) => {
                const {
                  ruralExceptionRequested,
                  ruralExceptionNote,
                  ...rest
                } = prev;
                return rest;
              });
              navigate("/sizers/dscr");
              enqueueSnackbar(
                "Duplicated — price it to save it as a new scenario",
                { variant: "success" },
              );
            }
          : null
      }
      values={form}
      baseline={baseline}
      onDiscard={() => {
        if (!baseline) return;
        setForm(baseline);
        setMissingRequirements([]);
        setRuralGate(null);
        setErr("");
      }}
      result={result}
      pricePending={busy}
      priceError={err ? { message: err } : null}
      onPrice={() => run()}
      blockedReason={blockedReason}
      liveNote="Priced against the published DSCR pricing sheet."
      /* No Save as deal: DSCR has no create-a-deal flow from the sizer yet, so
         the header omits the button rather than offer one that does nothing. */
      kpis={
        <DscrKpis
          result={result}
          scenario={shownScenario || pricedForm}
          version={loadedVersion}
        />
      }
      panels={
        <DscrPanels
          result={result}
          zip={(shownScenario || pricedForm)?.address?.zip}
        />
      }
      inputCells={scenarioInputSummary("dscr_quick", shownScenario || pricedForm)}
      form={
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            // Enter in a field submits — hold it to the same gate as the button.
            if (!busy && !blockedReason) run();
          }}
        >
          {/* Plain cards, never collapsible ones: pricing is an iterate loop,
              so the inputs stay put and open beside the answer. One card per
              subject — terms, borrower, collateral, income. */}
          <Flx column g={2.5}>
            <FormCard>
              <LoanDetailsSection
                form={form}
                set={set}
                correspondents={correspondents}
                gate={gateFields}
              />
            </FormCard>
            <FormCard>
              <BorrowerSection form={form} set={set} gate={gateFields} />
            </FormCard>
            <FormCard>
              <PropertySection form={form} set={set} gate={gateFields} />
            </FormCard>
            <FormCard>
              <IncomeExpensesSection form={form} set={set} gate={gateFields} />
            </FormCard>
          </Flx>

          {missingRequirements.length > 0 && (
            <Box sx={{ mt: 2.5 }}>
              <MissingRequirementsCallout items={missingRequirements} />
            </Box>
          )}

          {/* THE RURAL GATE'S ACTION lives here, with the form, because that is
              where the re-POST is. The determination itself renders in the rail.
              The note is required by the contract and rides onto the persisted
              scenario, so it is a real field, not a confirmation checkbox. */}
          {ruralGate && (
            <Box sx={{ mt: 2.5 }}>
              <Callout tone="warn" title="Rural property — exception required to price">
                <Box sx={{ mb: 1.25 }}>{ruralGate.proceed}</Box>
                <Box
                  component="textarea"
                  rows={2}
                  value={ruralNote}
                  onChange={(e) => setRuralNote(e.target.value)}
                  placeholder="Who is requesting the exception, and why"
                  sx={{
                    width: "100%",
                    resize: "vertical",
                    fontFamily: "inherit",
                    fontSize: 13,
                    color: SZ.ink900,
                    background: SZ.card,
                    border: `1px solid ${SZ.lineStrong}`,
                    borderRadius: SZ.radiusSm,
                    p: "8px 10px",
                    "&:focus": {
                      outline: "none",
                      borderColor: SZ.teal500,
                      boxShadow: "0 0 0 3px rgba(20,160,170,.15)",
                    },
                  }}
                />
                <Flx ac g={1.5} sx={{ mt: 1.25, flexWrap: "wrap" }}>
                  <SecondaryButton
                    disabled={busy || Boolean(blockedReason) || !ruralNote.trim()}
                    onClick={() => run({ requested: true, note: ruralNote.trim() })}
                  >
                    Request exception &amp; price
                  </SecondaryButton>
                  <ActionNote>
                    The exception is stored on the scenario and follows the loan.
                  </ActionNote>
                </Flx>
              </Callout>
            </Box>
          )}

          {/* Dev tooling last, under the form it acts on. */}

          {/* Live scenario state — admin only, off until the Form spy button
              above turns it on. */}
          <SizerStateSpy values={form} enabled={spyOpen} />
        </Box>
      }
      rail={
        <Flx column g={2}>
          {/* The form beside it is editable, so the answer says WHEN it was
              the answer. */}
          {pricedAt && result && (
            <Txt sx={{ fontSize: 11, color: SZ.ink400 }}>
              Priced {formatDateTime(pricedAt)}
              {pricedContext ? ` · ${pricedContext}` : ""}
            </Txt>
          )}
          <DscrResultsColumn
            result={result}
            pending={busy}
            /* Failures report in the re-price card above, beside the button;
               a failed RE-price leaves the previous answer standing here. */
            error={null}
            ruralGate={ruralGate}
            zip={form.address?.zip}
            onReanchor={blockedReason ? undefined : reanchor}
            pendingCoupon={reanchoring}
          />
        </Flx>
      }
    />
  );
}
